import * as React from "react";
import { useState, useEffect, useRef } from "react";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import {
  ReactifySearchProvider,
  Sensors,
  Filters,
  FiltersSelected,
  FilterProps,
  Results,
  SortBy as ReactifySortBy,
  ClearAll as ReactifyClearAll,
} from "@usereactify/search";
import { ProductTile } from "@project-stories/ecommerce/Product/ProductTile";
import CollectionBar from "./CollectionBar";
import './Collection.scss';
import ModelViewToggle from "@project-stories/client/ModelViewToggle/ModelViewToggle";
import {LoadMore} from "@project-stories/client/LoadMore/LoadMore";
import {SortBy} from "@project-stories/client/SortBy/SortBy";
import {ClearAll} from "@project-stories/client/ClearAll/ClearAll";
import useHeaderMenuStore from "@src/stores/HeaderMenuStore";
import useAnimationEnd from "@arctheme-hooks/useAnimationEnd";

export const Collection = ({ settings }) => {
  localStorage.setItem("debug", "reactify*");
  const [
    displayedProductsWithPromoBlocks,
    setDisplayedProductsWithPromoBlocks,
  ] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState([]);
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [isAnimatingSidebar, setIsAnimatingSidebar] = useState(false);
  const promoBlocks = useRef(settings.promotion_blocks);
  const paginationHaveLoaded = useRef(false);
  const isFiltering = useRef(false);
  const initialized = useRef(false);
  // const slidingDivRef: any = useRef(false);

  const openMenu = useHeaderMenuStore((state) => state.openMenu)
  const closeMenu = useHeaderMenuStore((state) => state.closeMenu)

  // const toggleSidebar = () => {
  //   setSidebarVisible((prevState) => {
  //     const newState = !prevState;
  //     document.body.classList.toggle("no-scroll__filters", newState);
  //     setIsAnimatingSidebar(true);
  //     return newState;
  //   });
  // };

  // useAnimationEnd(slidingDivRef, () => {
  //   if (!isSidebarVisible) {
  //     console.log('Animation END - CLOSE');
  //   } else {
  //     console.log('Animation END - OPEN');
  //   }
  //   setIsAnimatingSidebar(false);
  // });

  const handleProductsResults = (event) => {
    if (event.detail.type === "plp") {
      window.dispatchEvent(
        new CustomEvent("hide-spinner", {
          detail: {
            id: "spinner-collection",
          },
        }),
      );

      isFiltering.current = event.detail.isFiltering;

      if (isFiltering.current) {
        setProducts(event.detail.products);

        if (paginationHaveLoaded.current) {
          window.dispatchEvent(
            new CustomEvent("current-products-updated", {
              detail: {
                totalViewed: event.detail.products.length,
              },
            }),
          );
        } else {
          const intervalId = setInterval(() => {
            if (paginationHaveLoaded.current) {
              window.dispatchEvent(
                new CustomEvent("current-products-updated", {
                  detail: {
                    totalViewed: event.detail.products.length,
                  },
                }),
              );
              clearInterval(intervalId);
            }
          }, 100);
        }
      }

      const { products, page } = event.detail;

      if (!isFiltering.current) {
        isFiltering.current = false;
        const currentPage = page + 1;
        const supposedToBeCurrentlyDisplayed =
          currentPage * settings.products_per_page;

        // Update the state with the merged products
        const promoBlocksToBeInserted = getPromoBlocksToBeInserted(
          supposedToBeCurrentlyDisplayed,
        );

        const nextPageCount =
          supposedToBeCurrentlyDisplayed - promoBlocksToBeInserted.length;

        const productsToBeAdded = getProductsFromLoadedProducts(
          products,
          nextPageCount,
        );

        const updatedDisplayedProductsWithPromoBlocks = insertPromoBlocks(
          productsToBeAdded,
          promoBlocksToBeInserted,
        );
        setDisplayedProductsWithPromoBlocks(
          updatedDisplayedProductsWithPromoBlocks,
        );

        if (paginationHaveLoaded.current) {
          window.dispatchEvent(
            new CustomEvent("current-products-updated", {
              detail: {
                totalViewed: productsToBeAdded.length,
              },
            }),
          );
        } else {
          const intervalId = setInterval(() => {
            if (paginationHaveLoaded.current) {
              window.dispatchEvent(
                new CustomEvent("current-products-updated", {
                  detail: {
                    totalViewed: productsToBeAdded.length,
                  },
                }),
              );
              clearInterval(intervalId);
            }
          }, 100);
        }
      }
    }
  };

  function handlePaginationLoad() {
    paginationHaveLoaded.current = true;
  }

  const handleFiltersResults = (event) => {
    if (event.detail.type === "plp") {
      if (event.detail.filters) {
        setFilters(event.detail.filters);
      }
    }
  };


  const handleEscapeKey = async (event) => {
    if (event.key === "Escape") {
      setSidebarVisible(false);
    }
  };

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;

      window.dispatchEvent(new CustomEvent("collection-search-loaded"));

      window.addEventListener("pagination-have-loaded", handlePaginationLoad);
      window.addEventListener("product-list", handleProductsResults);
      window.addEventListener("filter-list", handleFiltersResults);
      window.addEventListener("keydown", handleEscapeKey);

      // Cleanup the event listeners on component unmount
      return () => {
        // window.removeEventListener(
        //   "pagination-have-loaded",
        //   handlePaginationLoad,
        // );
        // window.removeEventListener("product-list", handleProductsResults);
        // window.removeEventListener("filter-list", handleFiltersResults);
        // window.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, []);

  const getProductsFromLoadedProducts = (products, nextPageCount) => {
    return products.slice(0, nextPageCount);
  };

  const insertPromoBlocks = (productsToBeAdded, promoBlocksForNextPage) => {
    let productsCopy = productsToBeAdded.concat([]);
    promoBlocksForNextPage.forEach(({ position, block }) => {
      productsCopy.splice(position, 0, block);
    });

    return productsCopy;
  };

  const getPromoBlocksToBeInserted = (supposedToBeCurrentlyDisplayed) => {
    const blocksForNextPage: any = [];

    promoBlocks.current.forEach((block) => {
      block.position.sort((a, b) => a - b);
      block.position.forEach((position) => {
        if (position <= supposedToBeCurrentlyDisplayed) {
          blocksForNextPage.push({
            position: position - 1,
            block,
          });
        }
      });
    });

    return blocksForNextPage;
  };

  const gridColumns = isMobile ? 2 : 4;

  return (
    <>
      <ReactifySearchProvider
          mode="collection"
          collectionHandle={`${settings.collection.handle}`}
          shopifyPermanentDomain={`${settings?.shop?.domain}`}
          includeFields={[
            "*",
            // "handle",
            // "variants.option*", // returns variants.option1, variants.option2, etc...
          ]}

      >
        <div>
          <Sensors />
          <CollectionBar settings={settings} isMobile={isMobile} />
          <div>
              <Results
                  listStyle={{}}
                  listClassName={`grid grid-cols-${gridColumns} gap-[16px] lg:gap-[24px]`}
                  renderPaginationLoadMore={(props) => <LoadMore {...props} />}
                  renderResultCardProduct={(productProps) => {
                    return (
                      <ProductTile {...productProps} settings={settings} enableWishlist={true} />
                    )
                  }
              }
            />
          </div>
        </div>
      </ReactifySearchProvider>
    </>
  );
};

export default Collection;
